import {
  forwardRef,
  type KeyboardEvent as ReactKeyboardEvent,
  useMemo,
} from 'react';

import {
  Button,
  type ButtonOwnProps,
} from '@dynatrace/strato-components/buttons';
import { getIntentLink } from '@dynatrace-sdk/navigation';

import * as styles from './IntentButton.sty.js';
import { useIntent } from '../../core/hooks/intent.js';
import { type IntentWithoutResponseProps } from '../../core/slots/Intent/Intent.js';
import { AppIcon } from '../../layouts/app-header/app-icon/AppIcon.js';

//#region Typings
/** Accepted properties for the IntentButtonWithoutResponse. */
type IntentButtonWithoutResponseProps = Omit<
  IntentWithoutResponseProps,
  'onSelect'
> &
  Pick<
    ButtonOwnProps,
    | 'onClick'
    | 'color'
    | 'variant'
    | 'width'
    | 'aria-disabled'
    | 'textAlign'
    | 'size'
  > & {
    disabled?: boolean;
  };
//#endregion

/**
 * Renders a button that sends an intent without expecting a response.
 * Used when no `responseProperties` are provided.
 * @internal
 */
export const IntentButtonWithoutResponse = /* @__PURE__ */ forwardRef<
  HTMLAnchorElement,
  IntentButtonWithoutResponseProps
>((props, forwardedRef) => {
  const {
    payload,
    options,
    icon,
    children,
    disabled,
    onClick,
    size,
    textAlign,
    'aria-disabled': ariaDisabledProp,
    ...remainingProps
  } = props;

  const intent = useIntent({
    payload,
    options,
  });

  const link = useMemo(() => {
    return getIntentLink(
      payload,
      options.recommendedAppId ?? '',
      options.recommendedIntentId ?? '',
    );
  }, [options.recommendedAppId, options.recommendedIntentId, payload]);

  return (
    <Button
      as="a"
      // When disabled, set href undefined to prevent navigation.
      href={disabled ? undefined : link}
      ref={forwardedRef}
      onClick={(event) => {
        onClick?.(event);
        if (event.isDefaultPrevented()) {
          // already handled
          return;
        }

        const nativeEvent = event.nativeEvent;
        if (
          nativeEvent.button !== 0 ||
          nativeEvent.metaKey ||
          nativeEvent.ctrlKey ||
          nativeEvent.shiftKey
        ) {
          return; // let the browser handle the event
        }

        intent.send();
        event.stopPropagation();
        event.preventDefault();
      }}
      onKeyDown={(event: ReactKeyboardEvent) => {
        const nativeEvent = event.nativeEvent;

        if (nativeEvent.code !== 'Space' && nativeEvent.code !== 'Enter') {
          return; // let the browser handle the event
        }

        event.preventDefault();

        // open intent link in new tab while pressing ctrl/meta
        if (nativeEvent.metaKey || nativeEvent.ctrlKey) {
          window.open(link);
          return;
        }

        // open intent link in new window
        if (nativeEvent.shiftKey) {
          window.open(link, '_blank', 'menubar');
          return;
        }

        // let the shell handle the intent
        intent.send();
      }}
      disabled={disabled}
      aria-disabled={ariaDisabledProp}
      textAlign={textAlign}
      size={size}
      {...remainingProps}
    >
      {intent.iconURL && (
        <Button.Prefix>
          <AppIcon
            src={intent.iconURL}
            className={
              disabled || ariaDisabledProp
                ? styles.disabledAppIconOpacity
                : undefined
            }
            alt=""
          />
        </Button.Prefix>
      )}
      {icon && <Button.Prefix>{icon}</Button.Prefix>}
      {children}
    </Button>
  );
});
